import React, { useState, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import LanguageTabs from './CampaignForm/LanguageTabs'
import {
  AssetClass,
  campaignKeywords,
  generativeCreativeField,
  isCampaignOpen,
  isCampaignPublished,
  isCampaignRunning,
  validateCreative,
  validateLocalizations,
  validateMasterLanguage,
  getLocaleForLanguage,
} from 'api/models'

import { allFilesUploaded } from '../Media/media'
import Field, { ArrayField } from './CampaignForm/Field'

import DropDown from 'components/Shared/DropDown'
import ErrorMessage from 'components/Shared/ErrorMessage'
import InfoBox from 'components/Shared/InfoBox'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import Textarea from 'components/Shared/Textarea'

import { getUserReports } from 'api/userReports'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import useHasPermission, { hasPartialServiceAccessTier, Permissions } from 'hooks/useHasPermission'
import { fetchCampaignCtasAsync } from 'api/campaigns'
import constants from '../../../../constants'
import {
  arrayReducer,
  initStateArray,
  initStateObject,
  objectReducer,
  OVERWRITE,
} from 'reducers/default'
import { snakeCaseToPascalCase } from 'utils/helpers'
import { useCampaignSetup, useCampaignSetupDispatch } from '../Context/CampaignSetupContext'
import { hasEnabledVariations } from '../Context/disabledVariations'
import MediaUpload from '../Media/MediaUpload'
import AutoFill from './AutoFill'
import AudienceField from './CampaignForm/AudienceField'
import { creativeActions, creativeFormReducer } from './CampaignForm/creativeFormReducer'
import { DescriptionField } from './CampaignForm/DescriptionField'
import { Keywords } from './CampaignForm/Keywords'
import Targeting from './CampaignForm/Targeting'
import { validateLocationTargeting } from './CampaignForm/Targeting/targeting'
import { getRemovedField, parseCreatives } from './CampaignForm/utils'
import CoCreate from './CoCreate'
import { isEmpty } from 'lodash'

export default function CampaignForm({
  eventId,
  eventDate,
  eventInfo,
  campaign,
  goal,
  onCancel,
  onUpdate,
  showValidationErrors = false,
}) {
  const { t, i18n } = useTranslation()
  const { setup, loading: setupLoading, hasPendingMedia, error: setupError } = useCampaignSetup()
  const { overwriteTCCreatives } = useCampaignSetupDispatch()
  const hasCustomAudience = useMemo(() => {
    const tcCreative = setup.creatives.find((x) => x.tc === campaign.tc)
    return !!tcCreative?.audience_id
  }, [setup.creatives, campaign.tc])

  const partnerDetails =
    localStorage.getItem('partnerDetails') && JSON.parse(localStorage.getItem('partnerDetails'))
  const isITCRecommendations = ['ITC', 'ITC_v2'].includes(
    partnerDetails && partnerDetails.campaignRecommendationsModel,
  )

  const [validationErrorsMedia, setValidationErrorsMedia] = useState([])
  const [isValidFiles, setIsValidFiles] = useState(true)
  const errorRef = useRef(null)

  const [ctaOptions, dispatchCtaOptionsAction] = useReducer(arrayReducer, initStateArray)

  // state for managing languages
  const [chosenLanguages, setChosenLanguages] = useState([])
  const [activeLanguage, setActiveLanguage] = useState('en')
  const [minBodyLanguages, setMinBodyLanguages] = useState(3)
  const [minHeadlineLanguages, setMinHeadlineLanguages] = useState(3)
  const [invalidLanguageCodes, setInvalidLanguageCodes] = useState([])

  const [userReports, userReportsDispatch] = useReducer(objectReducer, initStateObject)
  const currentPublishedTargeting = useRef(setup.targeting)
  const hasPrismaAccess = hasPartialServiceAccessTier() || eventInfo.category === 'product'
  const hasWaveAccess = useHasPermission(Permissions.wave)

  const masterLang = chosenLanguages[0]?.value
  const isMaster = activeLanguage === masterLang

  // Ref for tracking last changes in localizations
  const lastLocalizationsRef = useRef({})
  const hasNormalizedInitialLanguage = useRef(false)

  // rebuild localizations in chosen order
  const rebuildLocalizationsInChosenOrder = useCallback((rawLocalizations, chosen) => {
    const ordered = {}
    for (let i = 0; i < chosen.slice(1).length; i++) {
      const lang = chosen.slice(1)[i]
      const key = getLocaleForLanguage(lang.value)
      const existing = rawLocalizations?.[key]
      ordered[key] = existing ? {
        headlines: [...(existing.headlines || [])],
        bodies: [...(existing.bodies || [])],
        description: existing.description || '',
        index: i + 1
      } : {
        headlines: ['', '', ''],
        bodies: ['', '', ''],
        description: '',
        index: i + 1
      }
    }
    return ordered
  }, [])

  const originalCreative = useMemo(() => {
    return parseCreatives(setup, campaign.tc, eventDate, hasPrismaAccess)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup, campaign.tc, eventDate])

  const [form, dispatchFormAction] = useReducer(creativeFormReducer, {
    ...initStateObject,
    content: originalCreative,
  })
  const sortedCtaOptions = [...ctaOptions.items]
    .map((option) => ({
      value: option,
      text: t([
        `Recommendations.campaign.setup.call_to_action.${option}`,
        snakeCaseToPascalCase(option),
      ]),
    }))
    .sort((a, b) => {
      const collator = new Intl.Collator(i18n.language, { sensitivity: 'base' })
      return collator.compare(a.text, b.text)
    })

  useEffect(() => {
    getUserReports(userReportsDispatch, eventId, {
      reportType: 'WRONG_TOPIC_RECOMMENDATION',
      tc: campaign.tc,
      tc_run_id: campaign.tc_run_id,
    })
  }, [eventId, campaign])

  const reportedKeywords = useMemo(() => {
    if (userReports.content?.items) {
      return userReports.content?.items.map((item) => item.keyword)
    }
  }, [userReports])

  useEffect(() => {
    const controller = new AbortController()
    const hasPixel = setup.integration_details.some((x) => x.asset_class === AssetClass.pixel)
    const leadFormId = setup.lead_form_id
    fetchCampaignCtasAsync(dispatchCtaOptionsAction, setup.goal ?? goal, hasPixel, leadFormId, controller)

    return () => controller.abort()
  }, [setup.goal, goal, setup.integration_details, setup.lead_form_id])

  useEffect(() => {
    // This is needed as defaults are assigned to some fields (cta, destination_url) by the context.
    dispatchFormAction({ type: OVERWRITE, payload: originalCreative })
  }, [originalCreative])


  const keywords = useMemo(() => campaignKeywords(campaign), [campaign])

  useEffect(() => {
    if ((form.error || setupError) && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [form.error, errorRef, setupError])

  // when the component mounts this sets mobile nav to display none
  // the setup started buttons were not showing above the navbar on some browser/ devices
  useEffect(() => {
    const mobileNav = document.getElementById('mobile-navbar')
    if (mobileNav?.style) {
      mobileNav.style.display = 'none'
    }

    return () => {
      const mobileNav = document.getElementById('mobile-navbar')
      if (mobileNav?.style) {
        mobileNav.style.display = 'block'
      }
    }
  }, [])

  const isRunning = isCampaignRunning(campaign, { validateNotEnded: true })
  const isPublished = isCampaignPublished(campaign)
  const isOpen = isCampaignOpen(campaign)
  const FieldError = ({ message }) => {
    if (!message) return null

    // Handle case where `message` is a single string
    if (typeof message === 'string') {
      return <div className="warning-red">{message}</div>
    }

    // Handle case where `message` is an array of strings
    if (Array.isArray(message)) {
      return (
        <div className="warning-red mb-2">
          {message.map((err, idx) => (
            <div className="m-1" key={idx}>
              {err}
            </div> // Render each message on a new line
          ))}
        </div>
      )
    }

    return <div className="warning-red">{message}</div>
  }

  FieldError.propTypes = {
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  }

  // functions for working with localizations
  const getLocalizationsFromForm = useCallback(() => {
    return form?.content?.localizations || {}
  }, [form?.content?.localizations])

  const handleChange = useCallback(
    (action, form, hasPendingMedia = false) => {
      dispatchFormAction(action)
      const newFields = creativeFormReducer(form, action).content
      const removedField = getRemovedField(form.content, newFields, action)

      overwriteTCCreatives({
        tc: campaign.tc,
        tc_run_id: campaign.tc_run_id,
        creatives: newFields,
        hasPendingMedia,
        creativeAction: {
          action,
          removedFields: [removedField],
        },
      })
    },
    [campaign.tc, campaign.tc_run_id, overwriteTCCreatives],
  )

  

  const handleLanguagesChanged = (newLanguages) => {
    setChosenLanguages(newLanguages)
    
    // first language - main fields, others - localizations
    const otherLanguages = newLanguages.slice(1)

    // update localizations only for other languages
    const currentLocalizations = getLocalizationsFromForm()
    const newLocalizations = { ...currentLocalizations }
    
    // remove localizations for languages that are no longer selected (except first)
    const otherLanguageCodes = otherLanguages.map(lang => lang.value)
    Object.keys(newLocalizations).forEach(locale => {
      const langCode = locale.split('_')[0]
      if (!otherLanguageCodes.includes(langCode)) {
        delete newLocalizations[locale]
      }
    })
    
    // add empty localizations for new languages (except first)
    const masterFields = getCurrentLanguageFields()
    otherLanguages.forEach((lang, index) => {
      const locale = getLocaleForLanguage(lang.value)
      if (!newLocalizations[locale]) {
        newLocalizations[locale] = {
          headlines: Array(masterFields.headline.length > 3 ? masterFields.headline.length : 3).fill(''),
          bodies: Array(masterFields.body.length > 3 ? masterFields.body.length : 3).fill(''),
          description: '',
          index: index + 1
        }
      }
    })
    
    // Rebuild in chosen order
    const orderedLocalizations = rebuildLocalizationsInChosenOrder(newLocalizations, newLanguages)
    
    lastLocalizationsRef.current = orderedLocalizations

    handleChange(
      { type: creativeActions.localizationsUpdate, payload: orderedLocalizations },
      form,
      false
    )
  }

  const handleActiveLanguageChange = useCallback((newLanguage) => {
    setActiveLanguage(newLanguage)
  }, [])

  const handleBatchChange = useCallback((actions, hasPendingMedia = false) => {
    let workingState = form
    for (const action of actions) {
      workingState = creativeFormReducer(workingState, action)
    }
    
    // trim localizations to the same length as the master language
    if (workingState.content.localizations && !isRunning) {
      const masterFilledCount = {
        body: (workingState.content.bodies || workingState.content.body || []).filter(Boolean).length,
        headline: (workingState.content.headlines || workingState.content.headline || []).filter(Boolean).length
      }
      
      const trimmedLocalizations = Object.fromEntries(
        Object.entries(workingState.content.localizations).map(([locale, data]) => [
          locale,
          {
            ...data,
            bodies: (data.bodies || []).slice(0, masterFilledCount.body),
            headlines: (data.headlines || []).slice(0, masterFilledCount.headline),
          }
        ])
      )
      
      workingState.content.localizations = trimmedLocalizations
    }
    
    dispatchFormAction({ type: OVERWRITE, payload: workingState.content })

    if (workingState.content.localizations) {
      lastLocalizationsRef.current = { ...workingState.content.localizations }
    }

    const removedField = getRemovedField(form.content, workingState.content, actions[actions.length - 1])
    
    overwriteTCCreatives({
      tc: campaign.tc,
      tc_run_id: campaign.tc_run_id,
      creatives: workingState.content,
      hasPendingMedia,
      creativeAction: {
        action: actions[actions.length - 1],
        removedFields: [removedField],
      },
    })
  }, [form, campaign.tc, campaign.tc_run_id, overwriteTCCreatives, isRunning])

  const updateLanguageField = useCallback((language, fieldType, value) => {
    const firstLanguage = chosenLanguages[0]
    const isFirstLanguage = firstLanguage && language === firstLanguage.value
    
    if (isFirstLanguage) {
      const actionType = fieldType === 'body' ? 'bodyUpdate' : 
        fieldType === 'headline' ? 'headlineUpdate' : 'descriptionUpdate'
      
      const action = {
        type: creativeActions[actionType],
        payload: value
      }

      handleChange(action, form, false)
    } else {
      // Processing other languages (LOCALIZATIONS)
      const locale = getLocaleForLanguage(language)
      const fieldKey = fieldType === 'body' ? 'bodies' : fieldType === 'headline' ? 'headlines' : 'description'
      
      // Получаем текущие localizations из form.content
      const currentLocalizations = form?.content?.localizations || {}
      
      // get base localization (from form.content or from lastLocalizationsRef)
      const baseLocalization = lastLocalizationsRef.current[locale] || currentLocalizations[locale] || {
        headlines: ['','',''],
        bodies: ['','',''],
        description: '',
        index: 1
      }
      
      // create new localization with updated field
      const newLocalization = {
        ...baseLocalization,
        [fieldKey]: value
      }
      
      
      // create new localizations for dispatch (deep copy to avoid read-only issues)
      const newLocalizations = {
        ...currentLocalizations,
        [locale]: { ...newLocalization }
      }
      
      // update ref for next call (create new object to avoid read-only issues)
      lastLocalizationsRef.current = { ...newLocalizations }

      handleChange(
        { type: creativeActions.localizationsUpdate, payload: newLocalizations },
        form,
        false
      )
    }
  }, [dispatchFormAction, chosenLanguages, form?.content?.localizations, handleChange, form])

  function getCurrentLanguageFields() {
    // if it is the first language (first tab), return main fields
    const firstLanguage = chosenLanguages[0]
    if (firstLanguage && activeLanguage === firstLanguage.value) {
      return {
        body: [...(form?.content?.body || [''])],
        headline: [...(form?.content?.headline || [''])],
        description: form?.content?.description || '',
      }
    }
    
    // for other languages take from localizations with last changes from ref
    const locale = getLocaleForLanguage(activeLanguage)
    const localization = form?.content?.localizations?.[locale] || {
      headlines: ['','',''],
      bodies: ['','',''],
      description: ''
    }
    
    // merge with last changes from ref
    const lastLocalization = lastLocalizationsRef.current[locale]
  
    const mergedLocalization = {
      ...localization,
      ...lastLocalization
    }
    
    return {
      body: [...(mergedLocalization.bodies || [''])],
      headline: [...(mergedLocalization.headlines || [''])],
      description: mergedLocalization.description || ''
    }
  }

  // Get count of filled fields in master language
  const getMasterFilledCount = useCallback(() => {
    const filledCount = {
      body: (form?.content?.bodies || form?.content?.body || []).filter(Boolean).length,
      headline: (form?.content?.headlines || form?.content?.headline || []).filter(Boolean).length
    }
    return filledCount
  }, [form?.content?.bodies, form?.content?.body, form?.content?.headlines, form?.content?.headline])

  useEffect(() => {
    if (masterLang) {
      const masterFields = getCurrentLanguageFields()
      setMinBodyLanguages(masterFields.body.length)
      setMinHeadlineLanguages(masterFields.headline.length)
    }
  }, [masterLang, form?.content?.bodies, form?.content?.headlines])

  useEffect(() => {
    if (form?.content?.language && form.success) {      
      const languagesFromForm = []
      
      // Add main language
      languagesFromForm.push(form.content.language)
      
      // Add languages from localizations sorted by index
      if (form.content.localizations) {
        const sortedEntries = Object.entries(form.content.localizations)
          .sort(([,a], [,b]) => (a.index || 0) - (b.index || 0))
        
        sortedEntries.forEach(([locale]) => {
          const langCode = locale.split('_')[0]
          if (!languagesFromForm.includes(langCode)) {
            languagesFromForm.push(langCode)
          }
        })
      }
      
      // Convert to needed format
      const formattedLanguages = languagesFromForm.map(language => ({
        text: `common.language.${language}`,
        value: language
      }))
      
      // update state
      setChosenLanguages(formattedLanguages)
    }
  }, [form?.content?.language, form?.content?.localizations, form.success])

  useEffect(() => {
    const defaultLang = form.content.language
    
    // initialize activeLanguage
    setActiveLanguage(defaultLang)

    if (form?.content?.localizations) {
      lastLocalizationsRef.current = { ...form.content.localizations }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Normalize initial language to 'cs' if it is 'cz'
  useEffect(() => {
    if (!hasNormalizedInitialLanguage.current && form?.content?.language === 'cz') {
      hasNormalizedInitialLanguage.current = true
      handleChange(
        { type: creativeActions.cocreateLanguageUpdate, payload: 'cs' },
        form,
        false,
      )
      setActiveLanguage('cs')
    }
  }, [form, form?.content?.language, handleChange])

  // Validate localizations and track invalid language codes
  useEffect(() => {
    const invalidCodes = []
    
    // Validate master language
    const masterValidation = validateMasterLanguage(form.content)
    if (!masterValidation.valid) {
      invalidCodes.push(...masterValidation.invalidLocales)
    }
    
    // Check localizations
    if (form?.content?.localizations && typeof form.content.localizations === 'object') {
      const masterFilledCount = getMasterFilledCount()
      const validationResult = validateLocalizations(form.content, masterFilledCount)
      
      if (!validationResult.valid) {
        // Convert locale codes (e.g., "de_DE") to language codes (e.g., "de")
        const invalidLocaleCodes = validationResult.invalidLocales.map(locale => locale.split('_')[0])
        invalidCodes.push(...invalidLocaleCodes)
      }
    }
    
    setInvalidLanguageCodes(invalidCodes)
  }, [form?.content?.localizations, form?.content?.headline, form?.content?.body, form?.content?.description, form?.content?.language, getMasterFilledCount])

  return (
    <>
      <div ref={errorRef} className="py-0" data-testid="campaign-form-error-banner">
        {(form.error || setupError) && (
          <ErrorMessage danger>{t(form.message || setupError)}</ErrorMessage>
        )}
      </div>
      <form className="campaign-form width-100" data-testid="campaign-form-test-id">
        <div className="d-flex flex-column justify-content-center">
          <Keywords
            eventId={eventId}
            setupProcessId={setup.id}
            campaign={campaign}
            isCampaignRunning={isRunning}
            reportedKeywords={reportedKeywords}
            keywords={keywords.headline}
            className="mb-0"
          />

          <span className={`autofill ${isITCRecommendations ? 'with-border' : ''}`}>
            <hr className="m-0 mx-3"></hr>
          </span>

          <span className="autofill_card p-3 mb-0">{t('Recommendations.campaign.setup.cocreate.description')}</span>
        </div>

        <div className="campaign-form__accent-background">
          <LanguageTabs
            activeLanguage={activeLanguage}
            chosenLanguages={chosenLanguages}
            onLanguageChange={handleActiveLanguageChange}
            mode = 'edit'
            isRunning={isRunning}
            invalidLocales={showValidationErrors ? invalidLanguageCodes : []}
            leadFormId={setup.lead_form_id}
            onLanguageAdd={(newLanguage) => {
              if (chosenLanguages.length >= 5) return
              const updatedLanguages = [...chosenLanguages, newLanguage]
              handleLanguagesChanged(updatedLanguages)
              handleActiveLanguageChange(newLanguage.value)
            }}
            onLanguageRemove={(languageToRemove) => {
              if (chosenLanguages.length <= 1) return
              
              const languageToRemoveIndex = chosenLanguages.findIndex(lang => lang.value === languageToRemove)
              const isRemovingFirstLanguage = languageToRemoveIndex === 0
              
              let updatedLanguages = chosenLanguages.filter(lang => lang.value !== languageToRemove)
              
              // If removing first language, make second language the new main language
              if (isRemovingFirstLanguage && updatedLanguages.length > 0) {
                const newMainLanguage = updatedLanguages[0].value
                const newMainLocale = getLocaleForLanguage(newMainLanguage)
                
                // Get current localizations
                const currentLocalizations = getLocalizationsFromForm()
                const newMainLocalization = currentLocalizations[newMainLocale] || {
                  headlines: ['', '', ''],
                  bodies: ['', '', ''],
                  description: ''
                }
                
                // Remove new main language from localizations
                const updatedLocalizations = { ...currentLocalizations }
                delete updatedLocalizations[newMainLocale]
                
                // Update lastLocalizationsRef immediately
                lastLocalizationsRef.current = updatedLocalizations
                
                // Use single action to change main language
                handleChange(
                  { 
                    type: creativeActions.changeMainLanguage, 
                    payload: {
                      newMainLanguage,
                      newMainLocalization,
                      updatedLocalizations
                    }
                  },
                  form,
                  false
                )
              } else {
                // Removing a non-first language → delete its localization and send to backend
                const localeToRemove = getLocaleForLanguage(languageToRemove)
                const currentLocalizations = getLocalizationsFromForm()
                if (currentLocalizations[localeToRemove]) {
                  const updatedLocalizations = { ...currentLocalizations }
                  delete updatedLocalizations[localeToRemove]
                  // sync ref and send to backend
                  lastLocalizationsRef.current = updatedLocalizations
                  handleChange(
                    { type: creativeActions.localizationsUpdate, payload: updatedLocalizations },
                    form,
                    false
                  )
                }
              }
              
              // Update languages list and active language
              setChosenLanguages(updatedLanguages)
              if (activeLanguage === languageToRemove) {
                setActiveLanguage(updatedLanguages[0].value)
              }
            }}
          />
        </div>

        <div className="campaign-form__section mb-5">
          {/* CoCreate for active language */}
          {(isOpen || isRunning) && (
            <div className="mb-4">
              <CoCreate
                eventId={eventId}
                tc={form?.content?.tc}
                tc_run_id={form?.content?.tc_run_id}
                fields={(() => {
                  if (isMaster) {
                    return {
                      bodies: getCurrentLanguageFields().body.length > 3 ? getCurrentLanguageFields().body : Array(3).fill(''), 
                      headlines: getCurrentLanguageFields().headline.length > 3 ? getCurrentLanguageFields().headline : Array(3).fill(''),
                      description: getCurrentLanguageFields().description
                    }
                  } else {
                    // For non-master languages, pass master's field count
                    const masterFilledCount = getMasterFilledCount()
                    return {
                      bodies: Array(masterFilledCount.body).fill(''),
                      headlines: Array(masterFilledCount.headline).fill(''),
                      description: ''
                    }
                  }
                })()}
                promptForConfirmation={getCurrentLanguageFields().body.filter(Boolean).length > 0}
                onChange={(generatedContent) => {
                  if (activeLanguage === chosenLanguages[0]?.value) {
                    handleChange(
                      { type: creativeActions.bodyHeadlineUpdate, payload: generatedContent },
                      form,
                      false
                    )
                  } else {
                    updateLanguageField(activeLanguage, 'headline', generatedContent.headline)
                    updateLanguageField(activeLanguage, 'body', generatedContent.body)
                    updateLanguageField(activeLanguage, 'description', generatedContent.description?.[0] || '')
                  }
                }}
                language={activeLanguage}
                onLanguageChanged={(lang) => {
                  const languageExists = chosenLanguages.find(l => l.value === lang)
                  
                  if (languageExists) {
                    // if language exists, switch to it
                    handleActiveLanguageChange(lang)
                  } else {
                    // replace current active tab with new language
                    const currentTabIndex = chosenLanguages.findIndex(l => l.value === activeLanguage)
                    
                    if (currentTabIndex !== -1) {
                      // create new language object
                      const newLanguage = {
                        text: `common.language.${lang}`,
                        value: lang
                      }
                      
                      // replace current tab with new language
                      const updatedLanguages = [...chosenLanguages]
                      updatedLanguages[currentTabIndex] = newLanguage
                      
                      // update list of languages and switch to new language
                      handleLanguagesChanged(updatedLanguages)
                      handleActiveLanguageChange(lang)

                      if (currentTabIndex === 0) {
                        handleChange(
                          { type: creativeActions.cocreateLanguageUpdate, payload: lang },
                          form,
                          false
                        )
                      }
                    }
                  }
                }}
                className={`${isITCRecommendations && isOpen ? 'with-border' : ''}`}
                chosenLanguages={chosenLanguages}
                disabled={isRunning && !isMaster}
              />
            </div>
          )}

          {/* Fields for active language */}
          <div className="language-content">
            <ArrayField
              as={Textarea}
              fieldName="body"
              form={getCurrentLanguageFields()}
              isDisabled={(index) => {
                const isCurrentlyMaster = activeLanguage === masterLang
                if (isRunning && !isCurrentlyMaster) {
                  return true
                }
                const filledCount = getMasterFilledCount().body
                return !isCurrentlyMaster && index >= filledCount
              }}
              onChange={(payload, index) => {
                const current = getCurrentLanguageFields()
                const prevLen = current.body.length
                let next = [...current.body]

                if (payload === null) next.splice(index, 1)
                else if (typeof index !== 'undefined' && index != null) next[index] = payload
                else next = payload

                if (isMaster) {
                  const actions = [{ type: creativeActions.bodyUpdate, payload: next }]

                  if (next.length !== prevLen && !isRunning) {
                    const currentLocalizations = getLocalizationsFromForm()
                    const updatedLocalizations = Object.fromEntries(
                      Object.entries(currentLocalizations).map(([locale, data]) => {
                        const curr = Array.isArray(data.bodies) ? data.bodies : []
                        const need = next.length
                        const resized =
                          curr.length > need
                            ? curr.slice(0, need)
                            : [...curr, ...Array(Math.max(0, need - curr.length)).fill('')]
                        return [locale, { ...data, bodies: resized }]
                      })
                    )
                    actions.push({ type: creativeActions.localizationsUpdate, payload: updatedLocalizations })
                  }

                  handleBatchChange(actions, false)
                  setMinBodyLanguages(next.length)
                } else {
                  updateLanguageField(activeLanguage, 'body', next)
                }
              }}
              isAddingEnabled={isMaster && !isRunning}
              isRemovingEnabled={isMaster && !isRunning}
              inputChildren={(index) =>
                index === 0 && (isOpen || isRunning) ? (
                  <AutoFill
                    eventId={eventId}
                    tc={form?.content?.tc}
                    tc_run_id={form?.content?.tc_run_id}
                    language={activeLanguage}
                    creativeFieldName={generativeCreativeField.bodies}
                    fields={(() => {
                      if (isMaster) {
                        return getCurrentLanguageFields().body.length > 3 ? getCurrentLanguageFields().body : Array(3).fill('')
                      } else {
                        // For non-master languages, pass master's field count
                        const masterFilledCount = getMasterFilledCount()
                        return Array(masterFilledCount.body).fill('')
                      }
                    })()}
                    promptForConfirmation={getCurrentLanguageFields().body.filter(Boolean).length > 0}
                    onChange={(generatedContent) => {
                      updateLanguageField(activeLanguage, 'body', generatedContent)
                    }}
                    disabled={isRunning && !isMaster}
                  />
                ) : null
              }
              restFn={(index) => ({
                wrapperClass:
                index === 0 && (isOpen || isRunning) ? 'generative-input align-bottom' : '',
                inputClass: index === 0 ? 'textarea-autofill' : '',
              })}
              minCount={isMaster ? 3 : minBodyLanguages}
              maxLength={280}
              maxLengthClassNamesFn={(length) =>
                length > 0 && length <= 200
                  ? 'green'
                  : length > 200 && length <= 250
                    ? 'orange'
                    : length > 250
                      ? 'red'
                      : ''
              }
              tooltip={{
                title: t('Recommendations.campaign.setup.body.title'),
                body: (
                  <div style={{ fontSize: '.875rem', fontWeight: 500 }}>
                    <p className="pb-2">{t('Tips.Recommendations.CreativeComposer.Body.1')}</p>
                    <p>{t('Tips.Recommendations.CreativeComposer.Body.2')}</p>
                  </div>
                ),
              }}
            >
              {isITCRecommendations ? null : (
                <Keywords
                  eventId={eventId}
                  setupProcessId={setup?.id}
                  campaign={campaign}
                  reportedKeywords={reportedKeywords}
                  keywords={keywords.body}
                />
              )}
            </ArrayField>

            <ArrayField
              fieldName="headline"
              form={getCurrentLanguageFields()}
              isDisabled={(index) => {
                const isCurrentlyMaster = activeLanguage === masterLang
                if (isRunning && !isCurrentlyMaster) {
                  return true
                }
                const filledCount = getMasterFilledCount().headline
                return !isCurrentlyMaster && index >= filledCount
              }}
              onChange={(payload, index) => {
                const current = getCurrentLanguageFields()
                const prevLen = current.headline.length
                let next = [...current.headline]

                if (payload === null) next.splice(index, 1)
                else if (typeof index !== 'undefined' && index != null) next[index] = payload
                else next = payload

                if (isMaster) {
                  const actions = [{ type: creativeActions.headlineUpdate, payload: next }]

                  if (next.length !== prevLen && !isRunning) {
                    const currentLocalizations = getLocalizationsFromForm()
                    const updatedLocalizations = Object.fromEntries(
                      Object.entries(currentLocalizations).map(([locale, data]) => {
                        const curr = Array.isArray(data.headlines) ? data.headlines : []
                        const need = next.length
                        const resized =
                          curr.length > need
                            ? curr.slice(0, need)
                            : [...curr, ...Array(Math.max(0, need - curr.length)).fill('')]
                        return [locale, { ...data, headlines: resized }]
                      })
                    )

                    actions.push({ type: creativeActions.localizationsUpdate, payload: updatedLocalizations })
                  }

                  handleBatchChange(actions, false)
                  setMinHeadlineLanguages(next.length)
                } else {
                  updateLanguageField(activeLanguage, 'headline', next)
                }
              }}
              isAddingEnabled={isMaster && !isRunning}
              isRemovingEnabled={isMaster && !isRunning}
              inputChildren={(index) =>
                index === 0 && (isOpen || isRunning) ? (
                  <AutoFill
                    eventId={eventId}
                    tc={form?.content?.tc}
                    tc_run_id={form?.content?.tc_run_id}
                    language={activeLanguage}
                    creativeFieldName={generativeCreativeField.headlines}
                    fields={(() => {
                      if (isMaster) {
                        return getCurrentLanguageFields().headline.length > 3 ? getCurrentLanguageFields().headline : Array(3).fill('')
                      } else {
                        // For non-master languages, pass master's field count
                        const masterFilledCount = getMasterFilledCount()
                        return Array(masterFilledCount.headline).fill('')
                      }
                    })()}
                    promptForConfirmation={getCurrentLanguageFields().headline.filter(Boolean).length > 0}
                    onChange={(generatedContent) => {
                      updateLanguageField(activeLanguage, 'headline', generatedContent)
                    }}
                    disabled={isRunning && !isMaster}
                  />
                ) : null
              }
              restFn={(index) => ({
                wrapperClass: index === 0 && (isOpen || isRunning) ? 'generative-input' : '',
                inputClass: index === 0 ? 'input-autofill' : '',
              })}
              minCount={isMaster ? 3 : minHeadlineLanguages}
              maxLength={80}
              maxLengthClassNamesFn={(length) =>
                length > 0 && length <= 40
                  ? 'green'
                  : length > 40 && length <= 60
                    ? 'orange'
                    : length > 60
                      ? 'red'
                      : ''
              }
              tooltip={{
                title: t('Recommendations.campaign.setup.headline.title'),
                body: (
                  <div style={{ fontSize: '.875rem', fontWeight: 500 }}>
                    <p className="pb-1">{t('Tips.Recommendations.CreativeComposer.Headline.1')}</p>
                    <p>{t('Tips.Recommendations.CreativeComposer.Headline.2')}</p>
                  </div>
                ),
              }}
            >
              {isITCRecommendations ? null : (
                <Keywords
                  eventId={eventId}
                  setupProcessId={setup.id}
                  campaign={campaign}
                  reportedKeywords={reportedKeywords}
                  keywords={keywords.headline}
                  className="first"
                />
              )}
            </ArrayField>

            <div className="mb-1">
              <DescriptionField
                form={getCurrentLanguageFields()}
                isCampaignPublished={isPublished}
                wrapperClass = {isOpen || isRunning ? 'generative-input' : ''}
                disabled={isRunning && !isMaster}
                inputChildren={() => 
                  (isOpen || isRunning) ? (
                    <AutoFill
                      eventId={eventId}
                      tc={form?.content?.tc}
                      tc_run_id={form?.content?.tc_run_id}
                      language={activeLanguage}
                      creativeFieldName={generativeCreativeField.description}
                      fields={[getCurrentLanguageFields().description].filter(Boolean)}
                      promptForConfirmation={getCurrentLanguageFields().description.length > 0}
                      onChange={(generatedContent) => {
                        updateLanguageField(activeLanguage, 'description', generatedContent[0] || 'description')
                      }}
                      disabled={isRunning && !isMaster}
                    />
                  ) : null
                }
                onChange={(payload) => {
                  updateLanguageField(activeLanguage, 'description', payload)
                }}
              />
            </div>
          </div>
        </div>   

        <div className="mb-5 creative-composer-section-wrapper creative-composer-section-wrapper__media">
          <InfoBox
            title="Media"
            body={
              <span
                dangerouslySetInnerHTML={{
                  __html: t('Recommendations.campaign.setup.media.info', {
                    interpolation: { escapeValue: false },
                  }),
                }}
              />
            }
          >
            <label className="fs-18 d-inline-block">Media</label>
          </InfoBox>
          {!isITCRecommendations && (
            <Keywords
              eventId={eventId}
              setupProcessId={setup.id}
              campaign={campaign}
              isCampaignRunning={isRunning}
              reportedKeywords={reportedKeywords}
              keywords={keywords.media}
              className="last"
            />
          )}

          <FieldError message={validationErrorsMedia.media} />

          <MediaUpload
            initialMedia={form.content.media}
            onChange={(
              files,
              removedIndex = null,
              hasPendingMedia = false,
              errorMessages = [],
              validFiles = true,
              isGroupUpload = true,
            ) => {
              handleChange(
                { type: creativeActions.mediaUpdate, payload: files, index: removedIndex },
                form,
                hasPendingMedia,
                errorMessages,
                validFiles,
              )
              setIsValidFiles(validFiles)
              if (errorMessages.length > 0) {
                setValidationErrorsMedia((prev) => ({
                  ...prev,
                  media: errorMessages.map((err) => err.errors).flat(),
                }))
              } else if (isEmpty(files) || (!isEmpty(files) && !isGroupUpload)) {
                setValidationErrorsMedia((prev) => ({
                  ...prev,
                  media: null,
                }))
              }
            }}
            maxItems={10}
            canAdd={!isRunning}
            canRemove={!isRunning && (!isPublished || form.content.media.length > 1)}
            canReplace={isPublished || (isRunning && form.content.media.length === 1)}
            validationError={
              validationErrorsMedia.media && validationErrorsMedia.media.length > 0 && !isValidFiles
            }
            isCampaignRunning={isRunning}
          />
        </div>

        <ArrayField
          fieldName="destination_url"
          form={form.content}
          minCount={1}
          onChange={(payload, index) =>
            handleChange(
              { type: creativeActions.urlUpdate, index, payload: payload !== null ? payload.trim() : null },
              form,
              setup.creatives,
              isRunning,
            )
          }
          isAddingEnabled={!isRunning}
          isRemovingEnabled={!isRunning}
          removeBtnClassNames="input__remove--r"
          maxLength={300}
          maxLengthClassNamesFn={(length) =>
            length > 0 && length <= 200
              ? 'green'
              : length > 200 && length <= 240
                ? 'orange'
                : length > 240
                  ? 'red'
                  : ''
          }
          validator={(val) => {
            return {
              isValid: val && constants.URL_VALIDATION_REGEX.test(val.trim()),
              errorMsg: t('common.errors.wrongUrlFormat'),
            }
          }}
          showPositiveFeedback={!isRunning && !isPublished}
          placeholder="https://www.your-website.com"
          validateOnBlur
          tooltip={{
            title: t('Tips.Recommendations.CreativeComposer.DestinationUrl.title'),
            body: (
              <ol
                style={{
                  listStyle: 'decimal',
                  paddingLeft: '1.4rem',
                  fontSize: '.875rem',
                  fontWeight: 500,
                }}
              >
                <li>{t('Tips.Recommendations.CreativeComposer.DestinationUrl.1')}</li>
                <li>{t('Tips.Recommendations.CreativeComposer.DestinationUrl.2')}</li>
                <li>{t('Tips.Recommendations.CreativeComposer.DestinationUrl.3')}</li>
              </ol>
            ),
          }}
          wrapperClass="input-cell--input-padding-max"
        />

        <div className="creative-composer-section-wrapper mb-5">
          <Field
            fieldName="call_to_action"
            as={DropDown}
            tooltip={{ content: 'Tips.Recommendations.CreativeComposer.CallToAction' }}
            value={form.content.call_to_action ?? ''}
            options={sortedCtaOptions.map((x) => ({ value: x.value, text: x.text }))}
            onChange={(payload) =>
              handleChange(
                { type: creativeActions.callToActionUpdate, payload },
                form,
                setup.creatives,
                isRunning,
              )
            }
            required={false} // For the dropdown. Otherwise a '*' is added when value is empty.
            classNames="cta-dropdown"
            disabled={isPublished}
            renderItem={(item) => {
              const option =
                sortedCtaOptions.find((x) => x.value === item?.value) ?? sortedCtaOptions[0]
              return option
                ? t([
                  `Recommendations.campaign.setup.call_to_action.${option.text}`,
                  snakeCaseToPascalCase(option.text),
                ])
                : '_'
            }}
          />
        </div>

        <div className="creative-composer-section-wrapper mb-5">
          <AudienceField
            audienceId={
              form.content?.saved_audience_id
                ? form.content.saved_audience_id
                : form.content?.audience_id
            }
            onChange={(payload, index) =>
              handleChange(
                { type: creativeActions.audienceUpdate, index, payload: payload },
                form,
                setup.creatives,
                isRunning,
              )
            }
            campaign={campaign}
          />
        </div>

        <div className="creative-composer-section-wrapper">
          <Targeting
            tc={campaign.tc}
            tc_run_id={campaign.tc_run_id}
            onChange={(targeting) => {
              // For editing a published campaign
              currentPublishedTargeting.current = targeting
            }}
            // Note: this only disables the dropdown.
            // If postal-codes, or other editable items are selected,
            // the user can still edit them.
            disabled={isPublished || form.content.saved_audience_id}
            savedAudienceSelected={!!form.content.saved_audience_id}
            showValidationErrors={!isOpen}
          />
        </div>



        <div>
          {form.loading || setupLoading ? (
            <div className="my-3">
              <LoadingSpinner data-testid="loading-spinner" title={t('common.sending')} />
            </div>
          ) : (
            <div className="creative-composer-controls-container">
              {isRunning ? (
                <div className="my-3">
                  <TransparentBgButton
                    text={t('common.cancel')}
                    onClick={() => {
                      dispatchFormAction({
                        type: OVERWRITE,
                        payload: { ...initStateObject, content: originalCreative },
                      })
                      if (onCancel) onCancel()
                    }}
                    color="navy"
                  />

                  <SecondaryButton
                    data-testid="save-campaign-form-btn"
                    text={t('common.update')}
                    onClick={(e) => {
                      e.preventDefault()
                      onUpdate()
                    }}
                    color="navy"
                    disabled={
                      form.loading ||
                      setupLoading ||
                      hasPendingMedia ||
                      !hasWaveAccess ||
                      !allFilesUploaded(form.content.media) ||
                      !validateCreative(form.content, { setupOnly: false }).valid ||
                      !hasEnabledVariations(setup.creatives) ||
                      !validateLocationTargeting(
                        currentPublishedTargeting.current,
                        campaign.tc,
                        campaign.tc_run_id,
                        hasCustomAudience,
                        !!form.content.saved_audience_id,
                      ).valid
                    }
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </form>
    </>
  )
}

CampaignForm.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  eventDate: PropTypes.string.isRequired,
  eventInfo: PropTypes.object,
  campaign: PropTypes.object.isRequired,
  goal: PropTypes.string,
  onCancel: PropTypes.func,
  onUpdate: PropTypes.func,
  onChange: PropTypes.func,
  showValidationErrors: PropTypes.bool,
}
